import React, { useState, useEffect, useMemo } from "react";
import { fetchAllUsers } from "../../services/userServices";
import { userActivation } from "../../services/adminService";
import Spinner from "../shared/Spinner";

function Users() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const itemsPerPage = 15;

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const data = await fetchAllUsers();

        // Remove sensitive fields
        const cleanedData = data.map(
          ({
            password,
            session_expire_time,
            otp_expired_date,
            session_token,
            otp_hash,
            ...safeData
          }) => safeData
        );

        setUsers(cleanedData);
      } catch (err) {
        setError("Failed to fetch users.");
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, []);

  const handleActivate = async (user) => {
    setDeactivatingId(user.id);
    try {
      await userActivation(user.id, user.contact_information, user.chat_id, 1);
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, is_active: true } : u))
      );
      if (selectedUser?.id === user.id) {
        setSelectedUser({ ...user, is_active: true });
      }
    } catch (err) {
      console.error("Error activating user", err);
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleDeactivate = async (user) => {
    setDeactivatingId(user.id);
    try {
      await userActivation(user.id, user.contact_information, user.chat_id, 0);
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, is_active: false } : u))
      );
      if (selectedUser?.id === user.id) {
        setSelectedUser({ ...user, is_active: false });
      }
    } catch (err) {
      console.error("Error deactivating user", err);
    } finally {
      setDeactivatingId(null);
    }
  };

  // Get unique roles from users
  const uniqueRoles = useMemo(() => {
    return [...new Set(users.map((user) => user.role).filter(Boolean))].sort();
  }, [users]);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      // Search filter
      const matchesSearch = (user.name || "").toLowerCase().includes(searchTerm.toLowerCase());
      
      // Status filter
      const matchesStatus = 
        statusFilter === "all" || 
        (statusFilter === "active" && user.is_active) ||
        (statusFilter === "deactivated" && !user.is_active);
      
      // Role filter
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      
      return matchesSearch && matchesStatus && matchesRole;
    });
  }, [users, searchTerm, statusFilter, roleFilter]);

  // Calculate paginated users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, roleFilter]);

  const formatLabel = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle row click
  const handleRowClick = (user) => {
    setSelectedUser(user);
  };

  // Early returns after all hooks
  if (loading) {
    return (
      <div className="relative min-h-[calc(100vh-8rem)]">
        <Spinner size="xl" fullScreen={true} />
      </div>
    );
  }
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="p-4 flex flex-col md:flex-row gap-4">
      {/* Left panel: user list */}
      <div className="w-full md:w-4/5">
        <h1 className="text-2xl font-bold mb-4">User Management</h1>
        
        {/* Search and Filters Section */}
        <div className="mb-4 space-y-3">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border p-2 w-full rounded"
          />
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Status Filter */}
            <div className="flex-1">
              <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border p-2 w-full rounded"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="deactivated">Deactivated</option>
              </select>
            </div>
            
            {/* Role Filter */}
            <div className="flex-1">
              <label htmlFor="roleFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                id="roleFilter"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="border p-2 w-full rounded"
              >
                <option value="all">All</option>
                {uniqueRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Pagination Controls */}
        {filteredUsers.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">
                  {filteredUsers.length === 0
                    ? 0
                    : (currentPage - 1) * itemsPerPage + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, filteredUsers.length)}
                </span>{" "}
                of <span className="font-medium">{filteredUsers.length}</span>{" "}
                results
              </div>

              {totalPages > 0 && (
                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    ‹
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1) ||
                        totalPages <= 5
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-2 text-sm border rounded-md transition-colors ${
                              currentPage === page
                                ? "bg-blue-600 text-white border-blue-600"
                                : "border-gray-300 hover:bg-gray-50 text-gray-700"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return (
                          <span key={page} className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="table-auto w-full bg-white text-sm shadow-lg">
            <thead>
              <tr>
                <th className="py-2 text-left">Name</th>
                <th className="py-2 text-left">Role</th>
                <th className="py-2 text-left">Status</th>
                <th className="py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    className="border-t hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(user)}
                  >
                    <td className="py-2">{user.name || "—"}</td>
                    <td className="py-2">{user.role || "—"}</td>
                    <td className="py-2">
                      {user.is_active ? "Active" : "Deactivated"}
                    </td>
                    <td className="py-2 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                      {user.is_active ? (
                        <button
                          onClick={() => handleDeactivate(user)}
                          className="px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                        >
                          {deactivatingId === user.id
                            ? "Deactivating…"
                            : "Deactivate"}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(user)}
                          className="py-1 w-20 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                        >
                          {deactivatingId === user.id
                            ? "Activating…"
                            : "Activate"}
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="py-1 w-20 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right panel: user details */}
      <div className="w-full md:w-1/2 md:sticky md:top-4 self-start border-2 rounded bg-gray-50 p-4 h-[70vh]">
        <div className="overflow-y-auto h-full pr-2">
          {selectedUser ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center border-b-2 pb-2 mb-3">
                <h2 className="text-xl font-semibold">User Details</h2>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-2">
                {Object.entries(selectedUser).map(([key, value]) => {
                  // Format value for display
                  let displayValue = value;
                  if (value === null || value === undefined) {
                    displayValue = "—";
                  } else if (typeof value === "boolean") {
                    displayValue = value ? "Yes" : "No";
                  } else if (typeof value === "object") {
                    displayValue = JSON.stringify(value, null, 2);
                  }
                  
                  return (
                    <div key={key} className="border-b pb-2 last:border-b-0">
                      <p className="text-sm">
                        <strong className="text-gray-700">{formatLabel(key)}:</strong>
                      </p>
                      <p className="text-gray-900 mt-1 break-words">{displayValue}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <p className="text-lg mb-2">Select a user to view details</p>
                <p className="text-sm">Click on any user row in the table</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Users;
